import { useEffect, useRef, useState } from "react";
import {
  Link,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  type DeliveryStockOptionDto,
  type DeliveryNoteDetailDto,
  type DeliveryStatus,
  type DeliveryNoteUpdateInput,
} from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useDeliveryNote,
  useDeliverySource,
  useCreateDeliveryShipment,
  useUpdateDeliveryNote,
} from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { TableShell, Th, Td, Tr } from "@/components/ui/Table";
import {
  SourceDocPicker,
  type DnRefType,
} from "@/components/logistics/SourceDocPicker";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { formatQty } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
import { translateStatus } from "@/domain/i18n";
const day = (value: number | null | undefined) =>
  value ? formatDate(value) : "";
export function DeliveryNoteEditorPage() {
  const { id } = useParams();
  const loc = useLocation();
  const result = useDeliveryNote(id);
  const { t } = useTranslation();
  if (id && result.isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (id && (result.error || !result.data))
    return (
      <PageBody>
        <ErrorState
          message={t("deliveryNotes.errorLoad")}
          onRetry={() => void result.refetch()}
        />
      </PageBody>
    );
  return (
    <DeliveryEditor
      key={`${id ?? "new"}:${new URLSearchParams(loc.search).get("sourceId") ?? ""}:${new URLSearchParams(loc.search).get("action") ?? ""}`}
      dn={result.data}
    />
  );
}
function DeliveryEditor({ dn }: { dn?: DeliveryNoteDetailDto }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const action = params.get("action");
  const correction = action === "correct";
  const initType: DnRefType =
    dn?.refType ??
    (params.get("refType") === "invoice" ? "invoice" : "packing");
  const initSource = dn?.sourceId ?? params.get("sourceId") ?? "";
  for (const k of ["action", "sourceId", "refType", "archived"])
    params.delete(k);
  const context = params.size ? `?${params}` : "";
  const [type, setType] = useState<DnRefType>(initType);
  const [pickerType, setPickerType] = useState<DnRefType>(initType);
  const [pendingSource, setPendingSource] = useState<{
    type: DnRefType;
    id: string;
  } | null>(null);
  const [sourceId, setSourceId] = useState(initSource);
  const source = useDeliverySource(type, sourceId || undefined, dn?.id);
  const base = useRef(dn);
  const loaded = useRef(false);
  const lock = useRef(false);
  const allow = useRef(false);
  const request = useRef(crypto.randomUUID());
  const initialStatus: DeliveryStatus =
    action === "dispatch"
      ? "In Transit"
      : action === "complete"
        ? "Delivered"
        : action === "cancel"
          ? "Cancelled"
          : (dn?.deliveryStatus ?? "Pending");
  const [f, setF] = useState({
    shipTo: dn?.shipTo ?? "",
    deliveryMode: dn?.deliveryMode ?? "",
    vehicleNo: dn?.vehicleNo ?? "",
    driverName: dn?.driverName ?? "",
    driverPhone: dn?.driverPhone ?? "",
    respBy: dn?.respBy ?? user?.displayName ?? "",
    remarks: dn?.remarks ?? "",
    receiverName: dn?.receiverName ?? "",
    deliveryDate: day(dn?.deliveryDate),
    deliveryStatus: initialStatus,
    reason: "",
  });
  const initial = useRef(f);
  // Xuất kho lúc hàng RỜI KHO (0088): chuyển Chờ giao → Đang giao thì mỗi dòng có số gửi phải chọn nguồn hàng — xuất từ
  // lô (trừ tồn đúng số gửi ngay khi lưu) hay giao thẳng có ghi chú.
  const dispatching = Boolean(dn) && !correction && dn?.deliveryStatus === "Pending" && f.deliveryStatus === "In Transit";
  // Phiếu đã Đang giao TRƯỚC quy tắc đó (xuất phát chưa ghi nguồn): chọn nguồn cho các dòng còn thiếu lúc xác nhận Đã giao.
  const legacyCompleting = Boolean(dn) && !correction && dn?.deliveryStatus === "In Transit" && f.deliveryStatus === "Delivered"
    && Boolean(dn?.items.some((it) => it.orderedQty > 0 && !it.fulfillment));
  const completing = dispatching || legacyCompleting;
  // Huỷ phiếu đang giao có dòng xuất kho: máy chủ đảo phiếu xuất bán, hàng về lại lô cũ — nói trước cho người huỷ biết.
  const cancellingIssued = Boolean(dn) && dn?.deliveryStatus === "In Transit" && f.deliveryStatus === "Cancelled" && Boolean(dn?.items.some((it) => it.fulfillment === "Stock"));
  // Sửa phiếu đã giao theo quy tắc mới (có dòng đã ghi nguồn hàng): dòng lúc xác nhận là 0 mà nay tăng số giao phải
  // chọn nguồn như lúc xác nhận — máy chủ đòi. Phiếu trước quy tắc (không dòng nào có nguồn) sửa như cũ.
  const correctingSource = Boolean(dn) && correction && dn?.deliveryStatus === "Delivered" && Boolean(dn?.items.some((it) => it.fulfillment));
  const stockOptions = useQuery({
    queryKey: ["dn-stock-options", dn?.id],
    enabled: (completing || correctingSource) && Boolean(dn?.id),
    queryFn: async ({ signal }) => (await apiFetch<{ data: DeliveryStockOptionDto[] }>(`/delivery-notes/${dn!.id}/stock-options`, { signal })).data,
  });
  const optionsFor = (lineNo: number) => stockOptions.data?.find((o) => o.lineNo === lineNo)?.positions ?? [];
  const [lines, setLines] = useState<
    {
      sourceItemId: string;
      lineNo: number;
      itemNo: string;
      subRef: string;
      ctnNo: string;
      description: string;
      unit: string;
      lotNumber: string;
      planned: string;
      received: string;
      remaining: number;
      remarks: string;
      /** Nguồn hàng khi xác nhận "Đã giao" (18/09/2026): xuất từ kho hay giao thẳng. */
      fulfillment: "" | "Stock" | "Direct";
      /** Vị trí lô xuất: `entryId|warehouseId|location`. */
      position: string;
      note: string;
    }[]
  >(
    dn?.items.map((it) => ({
      sourceItemId: it.originalItemId ?? "",
      lineNo: it.lineNo,
      itemNo: it.itemNo ?? "",
      subRef: it.subRef ?? "",
      ctnNo: it.ctnNo ?? "",
      description: it.description ?? "",
      unit: it.unit ?? "",
      lotNumber: it.lotNumber ?? "",
      planned: String(it.orderedQty),
      received: String(it.deliveredQty),
      remaining: it.orderedQty,
      remarks: it.remarks ?? "",
      fulfillment: it.fulfillment ?? "",
      position: "",
      note: it.fulfillmentNote ?? "",
    })) ?? [],
  );
  const initialLines = useRef(lines);
  /**
   * Dòng này phải chọn nguồn hàng ngay bây giờ: lúc xuất phát, mọi dòng có số gửi; phiếu xuất phát trước quy tắc, dòng còn
   * thiếu nguồn; khi sửa phiếu đã giao, dòng chưa có nguồn mà tăng số giao.
   */
  const needsSource = (l: { lineNo: number; received: string; planned: string }) => {
    const before = initialLines.current.find((p) => p.lineNo === l.lineNo);
    if (dispatching) return Number(l.planned) > 0;
    if (legacyCompleting) return Number(l.planned) > 0 && !before?.fulfillment;
    if (!correctingSource) return false;
    return Boolean(before) && !before!.fulfillment && Number(l.received) > Number(before!.received);
  };
  // Mặc định khi mở xuất phát: mã không có hàng trong kho → giao thẳng; có hàng → để người dùng tự chọn (không đoán thay),
  // nhưng chọn sẵn vị trí của lô cũ nhất CÒN ĐỦ số dự kiến giao (lô cũ nhất mà thiếu thì lưu sẽ bị từ chối).
  useEffect(() => {
    if (!completing || !stockOptions.data) return;
    setLines((prev) =>
      prev.map((l) => {
        const positions = stockOptions.data!.find((o) => o.lineNo === l.lineNo)?.positions ?? [];
        const first = positions.find((p) => p.available >= Number(l.planned)) ?? positions[0];
        return {
          ...l,
          fulfillment: l.fulfillment || (positions.length ? "" : "Direct"),
          position: l.position || (first ? JSON.stringify([first.entryId, first.warehouseId, first.location]) : ""),
        };
      }),
    );
  }, [completing, stockOptions.data]);
  const [dirty, setDirty] = useState(Boolean(action));
  // Phiếu nhiều dòng giao thẳng (thường gặp khi kho chưa có tồn theo lô): gõ ghi chú một lần, điền vào các dòng
  // còn trống — không tự điền ngầm, người dùng bấm và vẫn sửa được từng dòng.
  const [bulkNote, setBulkNote] = useState("");
  const directLines = lines.filter((l) => l.fulfillment === "Direct" && needsSource(l));
  const emptyDirect = directLines.filter((l) => !l.note.trim()).length;
  const [picker, setPicker] = useState(!dn && !initSource);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateDeliveryShipment();
  const update = useUpdateDeliveryNote(dn?.id ?? "new");
  const busy = create.isPending || update.isPending;
  const canWrite = Boolean(
    user && can(dn ? "records:update" : "records:create"),
  );
  const finalized = Boolean(
    dn &&
      (dn.deliveryStatus === "Cancelled" ||
        (dn.deliveryStatus === "Delivered" && !correction)),
  );
  useEffect(() => {
    if (
      dn ||
      loaded.current ||
      source.isFetching ||
      !source.data ||
      source.data.id !== sourceId
    )
      return;
    loaded.current = true;
    setF((prev) => ({ ...prev, shipTo: source.data!.shipTo ?? "" }));
    setLines(
      source.data.items.map((it, i) => ({
        sourceItemId: it.id,
        lineNo: i + 1,
        itemNo: it.itemNo ?? "",
        subRef: it.subRef ?? "",
        ctnNo: it.ctnNo ?? "",
        description: it.description ?? "",
        unit: it.unit ?? "",
        lotNumber: it.lotNumber ?? "",
        planned: String(it.remaining),
        received: "0",
        remaining: it.remaining,
        remarks: "",
        fulfillment: "" as const,
        position: "",
        note: "",
      })),
    );
    setDirty(true);
  }, [dn, source.data, sourceId, source.isFetching]);
  const blocker = useBlocker(() => !allow.current && (dirty || lock.current));
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (!allow.current && (dirty || lock.current)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty]);
  const change = (patch: Partial<typeof f>) => {
    setF((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setError(null);
  };
  usePageCrumbs(
    dn
      ? [
          { label: dn.dnNo, to: `/delivery-notes/${dn.id}${context}` },
          {
            label: t(correction ? "deliveryWorkspace.correct" : "common.edit"),
          },
        ]
      : [{ label: t("deliveryNotes.newDeliveryNote") }],
    `/delivery-notes${context}`,
  );
  const parseLines = () => {
    if (
      !lines.length ||
      lines.some(
        (l) =>
          !/^\d+$/.test(l.planned) ||
          !/^\d+$/.test(l.received) ||
          !Number.isSafeInteger(Number(l.planned)) ||
          !Number.isSafeInteger(Number(l.received)) ||
          Number(l.received) > Number(l.planned),
      )
    )
      throw new Error(t("deliveryWorkspace.delivery_quantity_invalid"));
    return lines.map((l) => ({
      ...l,
      plannedQty: Number(l.planned),
      actual: Number(l.received),
    }));
  };
  const save = async () => {
    if (lock.current || !canWrite || finalized) return;
    setError(null);
    try {
      const parsed = parseLines();
      if (!f.shipTo.trim())
        throw new Error(t("deliveryWorkspace.recipientRequired"));
      lock.current = true;
      if (dn) {
        const payload: DeliveryNoteUpdateInput = {
          expectedUpdatedAt: base.current?.updatedAt,
          reason: f.reason || undefined,
          correction,
        };
        for (const key of [
          "shipTo",
          "deliveryMode",
          "vehicleNo",
          "driverName",
          "driverPhone",
          "respBy",
          "remarks",
          "receiverName",
        ] as const)
          if (f[key] !== initial.current[key]) payload[key] = f[key];
        if (f.deliveryStatus !== dn.deliveryStatus)
          payload.deliveryStatus = f.deliveryStatus;
        if (f.deliveryDate !== initial.current.deliveryDate)
          payload.deliveryDate = f.deliveryDate
            ? new Date(f.deliveryDate + "T00:00:00").getTime()
            : null;
        payload.items = parsed
          .filter((l) => {
            const prev = initialLines.current.find(
              (p) => p.lineNo === l.lineNo,
            );
            return (
              !prev ||
              l.planned !== prev.planned ||
              l.received !== prev.received ||
              l.remarks !== prev.remarks
            );
          })
          .map((l) => ({
            lineNo: l.lineNo,
            orderedQty: l.plannedQty,
            deliveredQty: l.actual,
            remarks:
              l.remarks !==
              initialLines.current.find((row) => row.lineNo === l.lineNo)
                ?.remarks
                ? l.remarks
                : undefined,
          }));
        if (completing || correctingSource) {
          // Dòng cần nguồn phải kèm nguồn hàng (xuất phát: theo số gửi; sửa phiếu: dòng tăng số giao); máy chủ kiểm lại
          // và xuất kho trong cùng lần lưu.
          const targets = parsed.filter((l) => needsSource(l) && (completing ? l.plannedQty > 0 : l.actual > 0));
          const missing = targets.find(
            (l) => !l.fulfillment || (l.fulfillment === "Stock" && !l.position) || (l.fulfillment === "Direct" && l.note.trim().length < 5),
          );
          if (missing)
            throw new Error(t(!missing.fulfillment ? "deliveryWorkspace.delivery_fulfillment_required"
              : missing.fulfillment === "Stock" ? "deliveryWorkspace.delivery_stock_position_required" : "deliveryWorkspace.delivery_direct_note_required"));
          const byLine = new Map((payload.items ?? []).map((it) => [it.lineNo, it]));
          for (const l of targets) {
            const base = byLine.get(l.lineNo) ?? { lineNo: l.lineNo, orderedQty: l.plannedQty, deliveredQty: l.actual };
            const [entryId, warehouseId, location] = l.fulfillment === "Stock" ? (JSON.parse(l.position) as string[]) : [];
            byLine.set(l.lineNo, l.fulfillment === "Stock"
              ? { ...base, fulfillment: "Stock", entryId, warehouseId, location }
              : { ...base, fulfillment: "Direct", fulfillmentNote: l.note.trim() });
          }
          payload.items = [...byLine.values()];
        }
        await update.mutateAsync(payload);
        allow.current = true;
        navigate(`/delivery-notes/${dn.id}${context}`);
      } else {
        if (!source.data || source.isFetching)
          throw new Error(t("deliveryWorkspace.sourceLoading"));
        const result = await create.mutateAsync({
          refType: type,
          sourceId,
          requestId: request.current,
          expectedSourceUpdatedAt: source.data.updatedAt,
          shipTo: f.shipTo,
          deliveryMode: f.deliveryMode,
          vehicleNo: f.vehicleNo,
          driverName: f.driverName,
          driverPhone: f.driverPhone,
          respBy: f.respBy,
          remarks: f.remarks,
          items: parsed
            .filter((l) => l.plannedQty > 0)
            .map((l) => ({
              sourceItemId: l.sourceItemId,
              plannedQty: l.plannedQty,
            })),
        });
        allow.current = true;
        navigate(
          `/delivery-notes/${result.data.id}${result.data.deletedAt ? "?archived=true" : context}`,
        );
      }
    } catch (err) {
      setError(
        err instanceof Error && !("code" in err)
          ? err.message
          : getLocalizedError(err, t, "deliveryNotes.errorSave"),
      );
      setConfirm(false);
      if (!dn) void source.refetch();
    } finally {
      lock.current = false;
    }
  };
  const operational = dn?.deliveryStatus ?? "Pending";
  const statuses: DeliveryStatus[] = correction
    ? ["Delivered"]
    : operational === "Pending"
      ? ["Pending", "In Transit", "Cancelled"]
      : ["In Transit", "Delivered", "Cancelled"];
  return (
    <PageBody>
      <h1 className="text-xl font-semibold">
        {t(
          !dn
            ? "deliveryNotes.newDeliveryNote"
            : correction
              ? "deliveryWorkspace.correct"
              : "deliveryWorkspace.edit",
        )}{" "}
        {dn && <span className="font-mono">{dn.dnNo}</span>}
      </h1>
      <p className="mt-1 text-sm text-ink-soft">
        {t("deliveryWorkspace.editorHint")}
      </p>
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
      <ConfirmDialog
        open={confirm}
        tone="warn"
        title={t(
          correction
            ? "deliveryWorkspace.correct"
            : "deliveryWorkspace.confirmAction",
        )}
        description={`${dn?.dnNo ?? source.data?.number ?? ""} · ${translateStatus(t, f.deliveryStatus)}. ${t("deliveryWorkspace.confirmHint")}`}
        confirmLabel={t("common.save")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => setConfirm(false)}
        onConfirm={() => void save()}
      />
      {finalized && (
        <p className="mt-3 text-sm text-warn">
          {t("deliveryWorkspace.delivery_finalized")}
        </p>
      )}
      {!dn && (
        <Panel className="mt-4 flex items-center justify-between gap-3 p-4">
          <span className="font-mono text-brand">
            {source.data?.number || t("deliveryNotes.selectReference")}
          </span>
          <Button
            disabled={busy}
            variant="secondary"
            onClick={() => {
              setPickerType(type);
              setPicker(true);
            }}
          >
            {t("deliveryWorkspace.chooseSource")}
          </Button>
        </Panel>
      )}
      {dn && (
        <p className="mt-3 font-mono text-sm">
          {dn.plNo ?? dn.invoiceNo} · {dn.piNo}
        </p>
      )}
      {!dn && source.data && (
        <Panel className="mt-3 p-3">
          <p className="text-sm">{source.data.customerName}</p>
          <div className="mt-2 flex flex-wrap gap-3">
            {source.data.existing.map((d) => (
              <Link
                className="font-mono text-xs text-brand underline"
                to={`/delivery-notes/${d.id}${d.archived ? "?archived=true" : ""}`}
                key={d.id}
              >
                {d.dnNo} · {translateStatus(t, d.status)}
                {d.archived ? ` · ${t("invoiceWorkspace.archived")}` : ""}
              </Link>
            ))}
          </div>
        </Panel>
      )}
      {!dn && source.isLoading ? (
        <LoadingState />
      ) : !dn && source.error ? (
        <ErrorState
          message={getLocalizedError(
            source.error,
            t,
            "deliveryNotes.errorLoad",
          )}
          onRetry={() => void source.refetch()}
        />
      ) : null}
      {!dn &&
        source.data &&
        source.data.items.every((it) => it.remaining === 0) && (
          <p className="my-3 text-sm text-warn">
            {t("deliveryWorkspace.delivery_no_remaining")}
          </p>
        )}
      <fieldset disabled={!canWrite || finalized || busy} className="min-w-0">
        <Panel className="mt-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="sm:col-span-2 lg:col-span-3">
              <FieldLabel htmlFor="delivery-shipto">
                {t("deliveryNotes.shipTo")}
              </FieldLabel>
              <textarea
                id="delivery-shipto"
                rows={4}
                value={f.shipTo}
                onChange={(e) => change({ shipTo: e.target.value })}
                className="w-full rounded-sm border border-line bg-surface p-3 text-sm"
              />
            </div>
            {dn && (
              <div>
                <FieldLabel>{t("deliveryNotes.deliveryStatus")}</FieldLabel>
                <Select
                  aria-label={t("deliveryNotes.deliveryStatus")}
                  value={f.deliveryStatus}
                  disabled={correction}
                  onChange={(e) =>
                    change({ deliveryStatus: e.target.value as DeliveryStatus })
                  }
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {translateStatus(t, s)}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {(
              [
                "deliveryMode",
                "vehicleNo",
                "driverName",
                "driverPhone",
                "respBy",
              ] as const
            ).map((key) => (
              <div key={key}>
                <FieldLabel htmlFor={`delivery-${key}`}>
                  {t(key === "respBy" ? "pi.respBy" : `deliveryNotes.${key}`)}
                </FieldLabel>
                <Input
                  id={`delivery-${key}`}
                  value={f[key]}
                  onChange={(e) => change({ [key]: e.target.value })}
                />
              </div>
            ))}
            {dn && (
              <>
                <div>
                  <FieldLabel htmlFor="delivery-date">
                    {t("deliveryWorkspace.receiptDate")}
                  </FieldLabel>
                  <Input
                    id="delivery-date"
                    type="date"
                    value={f.deliveryDate}
                    onChange={(e) => change({ deliveryDate: e.target.value })}
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="delivery-receiver">
                    {t("deliveryWorkspace.receiver")}
                  </FieldLabel>
                  <Input
                    id="delivery-receiver"
                    value={f.receiverName}
                    onChange={(e) => change({ receiverName: e.target.value })}
                  />
                </div>
              </>
            )}
            <div className="sm:col-span-2 lg:col-span-3">
              <FieldLabel htmlFor="delivery-remarks">
                {t("common.remarks")}
              </FieldLabel>
              <textarea
                id="delivery-remarks"
                rows={2}
                value={f.remarks}
                onChange={(e) => change({ remarks: e.target.value })}
                className="w-full rounded-sm border border-line bg-surface p-3 text-sm"
              />
            </div>
            {dn && (
              <div className="sm:col-span-2 lg:col-span-3">
                <FieldLabel htmlFor="delivery-reason">
                  {t("deliveryWorkspace.reason")}
                </FieldLabel>
                <textarea
                  id="delivery-reason"
                  rows={2}
                  value={f.reason}
                  onChange={(e) => change({ reason: e.target.value })}
                  className="w-full rounded-sm border border-line bg-surface p-3 text-sm"
                />
              </div>
            )}
          </div>
        </Panel>
        {cancellingIssued && (
          <p className="mt-3 rounded-sm bg-warn-tint px-3 py-2 text-xs text-ink">{t("deliveryWorkspace.cancelReversesStock")}</p>
        )}
        {(completing || (correctingSource && lines.some(needsSource))) && (
          <div className="mt-3 rounded-sm bg-brand-tint px-3 py-2 text-xs text-ink">
            <p>{t(dispatching ? "deliveryWorkspace.fulfillmentHint" : legacyCompleting ? "deliveryWorkspace.legacySourceHint" : "deliveryWorkspace.correctionSourceHint")}</p>
            {stockOptions.isError && (
              <p role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-danger">
                {t("deliveryWorkspace.stockOptionsError")}
                <Button variant="secondary" size="sm" onClick={() => void stockOptions.refetch()}>{t("deliveryWorkspace.retry")}</Button>
              </p>
            )}
            {directLines.length >= 2 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label htmlFor="delivery-bulk-note" className="font-medium">{t("deliveryWorkspace.bulkDirectNote")}</label>
                <Input
                  id="delivery-bulk-note"
                  className="h-7 w-80 text-xs"
                  placeholder={t("deliveryWorkspace.directNotePlaceholder")}
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={bulkNote.trim().length < 5 || emptyDirect === 0}
                  onClick={() => {
                    const note = bulkNote.trim();
                    setLines((prev) => prev.map((r) => (r.fulfillment === "Direct" && needsSource(r) && !r.note.trim() ? { ...r, note } : r)));
                    setDirty(true);
                  }}
                >
                  {t("deliveryWorkspace.applyDirectNote", { count: emptyDirect })}
                </Button>
              </div>
            )}
          </div>
        )}
        <Panel className="mt-3">
          <TableShell className="border-0">
            <thead>
              <tr>
                <Th pin>
                  {t("deliveryNotes.itemNo")} / {t("common.subRef")}
                </Th>
                <Th>{t("common.description")}</Th>
                <Th>{t("deliveryNotes.ctnNo")}</Th>
                <Th>{t("lot.number")}</Th>
                <Th>{t("deliveryWorkspace.remaining")}</Th>
                <Th>{t("deliveryWorkspace.planned")}</Th>
                <Th>{t("deliveryWorkspace.received")}</Th>
                <Th>{t("common.unit")}</Th>
                {(completing || dn?.deliveryStatus === "Delivered" || dn?.deliveryStatus === "In Transit") && <Th>{t("deliveryWorkspace.fulfillment")}</Th>}
                {(completing || dn?.deliveryStatus === "Delivered" || dn?.deliveryStatus === "In Transit") && <Th>{t("deliveryWorkspace.stockLot")}</Th>}
                {dn && <Th>{t("common.lineRemark")}</Th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <Tr key={line.sourceItemId || line.lineNo}>
                  <Td pin className="font-mono text-xs">
                    {line.itemNo} / {line.subRef}
                  </Td>
                  <Td>
                    <p className="max-w-64 whitespace-normal text-xs">
                      {line.description}
                    </p>
                  </Td>
                  <Td className="font-mono text-xs">{line.ctnNo || "—"}</Td>
                  <Td className="font-mono text-xs">{line.lotNumber || "—"}</Td>
                  <Td className="font-mono">
                    {source.data?.items.find(
                      (it) => it.id === line.sourceItemId,
                    )?.remaining ?? line.remaining}
                  </Td>
                  <Td>
                    <Input
                      aria-label={`${t("deliveryWorkspace.planned")} ${i + 1}`}
                      className="w-24 font-mono"
                      inputMode="numeric"
                      disabled={!!dn && operational !== "Pending"}
                      value={line.planned}
                      onChange={(e) => {
                        setLines((prev) =>
                          prev.map((r, j) =>
                            j === i ? { ...r, planned: e.target.value } : r,
                          ),
                        );
                        setDirty(true);
                      }}
                    />
                  </Td>
                  <Td>
                    <Input
                      aria-label={`${t("deliveryWorkspace.received")} ${i + 1}`}
                      className="w-24 font-mono"
                      inputMode="numeric"
                      disabled={
                        !dn ||
                        f.deliveryStatus === "Pending" ||
                        f.deliveryStatus === "Cancelled"
                      }
                      value={line.received}
                      onChange={(e) => {
                        setLines((prev) =>
                          prev.map((r, j) =>
                            j === i ? { ...r, received: e.target.value } : r,
                          ),
                        );
                        setDirty(true);
                      }}
                    />
                  </Td>
                  <Td className="text-xs">{line.unit}</Td>
                  {needsSource(line) && (
                    <>
                      <Td>
                        <Select
                          aria-label={`${t("deliveryWorkspace.fulfillment")} ${i + 1}`}
                          containerClassName="w-48"
                          value={line.fulfillment}
                          onChange={(e) => {
                            const value = e.target.value as "" | "Stock" | "Direct";
                            setLines((prev) => prev.map((r, j) => (j === i ? { ...r, fulfillment: value } : r)));
                            setDirty(true);
                          }}
                        >
                          <option value="">{t("deliveryWorkspace.fulfillmentChoose")}</option>
                          {optionsFor(line.lineNo).length > 0 && <option value="Stock">{t("deliveryWorkspace.fulfillmentStock")}</option>}
                          <option value="Direct">{t("deliveryWorkspace.fulfillmentDirect")}</option>
                        </Select>
                      </Td>
                      <Td>
                        {line.fulfillment === "Stock" ? (
                          <Select
                            aria-label={`${t("deliveryWorkspace.stockLot")} ${i + 1}`}
                            containerClassName="w-80"
                            value={line.position}
                            onChange={(e) => {
                              const value = e.target.value;
                              setLines((prev) => prev.map((r, j) => (j === i ? { ...r, position: value } : r)));
                              setDirty(true);
                            }}
                          >
                            {optionsFor(line.lineNo).map((p) => (
                              <option key={`${p.entryId}|${p.warehouseId}|${p.location}`} value={JSON.stringify([p.entryId, p.warehouseId, p.location])}>
                                {t("deliveryWorkspace.positionOption", { lot: p.lotNumber, warehouse: p.warehouseName, location: p.location, available: formatQty(p.available) })}
                              </option>
                            ))}
                          </Select>
                        ) : line.fulfillment === "Direct" ? (
                          <div className="w-80">
                            <Input
                              aria-label={`${t("deliveryWorkspace.stockLot")} ${i + 1}`}
                              className="w-80"
                              placeholder={t("deliveryWorkspace.directNotePlaceholder")}
                              value={line.note}
                              onChange={(e) => {
                                const value = e.target.value;
                                setLines((prev) => prev.map((r, j) => (j === i ? { ...r, note: value } : r)));
                                setDirty(true);
                              }}
                            />
                            {/* Kho đang có hàng mã này mà chọn giao thẳng: không chặn, nhưng nói trước là người Duyệt sẽ xem lại. */}
                            {optionsFor(line.lineNo).length > 0 && (
                              <p className="mt-1 whitespace-normal text-2xs text-warn">
                                {t("deliveryWorkspace.directWithStockWarning", { qty: formatQty(optionsFor(line.lineNo).reduce((sum, p) => sum + p.available, 0)) })}
                              </p>
                            )}
                          </div>
                        ) : optionsFor(line.lineNo).length === 0 && !stockOptions.isLoading && !stockOptions.isError ? (
                          <span className="text-xs text-ink-faint">{t("deliveryWorkspace.stockNone")}</span>
                        ) : null}
                      </Td>
                    </>
                  )}
                  {!needsSource(line) && (dn?.deliveryStatus === "Delivered" || dn?.deliveryStatus === "In Transit") && (
                    <>
                      <Td className="text-xs">
                        {line.fulfillment ? t(line.fulfillment === "Stock" ? "deliveryWorkspace.fulfillmentStock" : "deliveryWorkspace.fulfillmentDirect") : "—"}
                      </Td>
                      <Td className="text-xs">
                        {line.fulfillment === "Direct" ? line.note : line.fulfillment === "Stock" ? line.lotNumber : "—"}
                        <DirectStockFlag item={dn?.items.find((it) => it.lineNo === line.lineNo)} />
                      </Td>
                    </>
                  )}
                  {dn && (
                    <Td>
                      <Input
                        aria-label={`${t("common.lineRemark")} ${i + 1}`}
                        value={line.remarks}
                        onChange={(e) => {
                          setLines((prev) =>
                            prev.map((r, j) =>
                              j === i ? { ...r, remarks: e.target.value } : r,
                            ),
                          );
                          setDirty(true);
                        }}
                      />
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </TableShell>
        </Panel>
      </fieldset>
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="sticky bottom-0 mt-4 flex justify-end gap-2 border-t border-line bg-surface p-3">
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() =>
            navigate(
              dn
                ? `/delivery-notes/${dn.id}${context}`
                : `/delivery-notes${context}`,
            )
          }
        >
          {t("common.cancel")}
        </Button>
        <Button
          disabled={
            !canWrite ||
            finalized ||
            busy ||
            !dirty ||
            !lines.length ||
            (!dn &&
              (!source.data ||
                source.isFetching ||
                lines.every((l) => Number(l.planned) <= 0)))
          }
          onClick={() => {
            try {
              parseLines();
              if (dn) setConfirm(true);
              else void save();
            } catch (err) {
              setError((err as Error).message);
            }
          }}
        >
          {t(busy ? "common.saving" : dn ? "common.save" : "common.create")}
        </Button>
      </div>
      <ConfirmDialog
        open={pendingSource !== null}
        tone="warn"
        title={t("common.unsavedTitle")}
        description={t("deliveryWorkspace.replaceSourceHint")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => setPendingSource(null)}
        onConfirm={() => {
          if (pendingSource) {
            loaded.current = false;
            setType(pendingSource.type);
            setSourceId(pendingSource.id);
            setLines([]);
            setF((prev) => ({ ...prev, shipTo: "" }));
            setPendingSource(null);
          }
        }}
      />
      {picker && (
        <Modal
          className="max-w-5xl"
          title={t("deliveryWorkspace.chooseSource")}
          closeLabel={t("common.close")}
          onClose={() => setPicker(false)}
        >
          <div className="p-4">
            <SourceDocPicker
              refType={pickerType}
              onRefTypeChange={setPickerType}
              onSelect={(id) => {
                if (sourceId && dirty) {
                  setPendingSource({ type: pickerType, id });
                  setPicker(false);
                  return;
                }
                loaded.current = false;
                setType(pickerType);
                setSourceId(id);
                setPicker(false);
                setError(null);
              }}
              busy={busy}
            />
          </div>
        </Modal>
      )}
    </PageBody>
  );
}

/**
 * Dòng giao thẳng trong lúc kho đang có hàng mã đó (0088): hiện số tồn lúc chọn và đã có người Duyệt xem lại chưa.
 * Nút ghi nhận nằm ở trang chi tiết phiếu giao; ở đây chỉ để người sửa phiếu thấy.
 */
export function DirectStockFlag({ item }: { item?: { fulfillment?: string | null; directStockQuantity?: number | null; directReviewedBy?: string | null } }) {
  const { t } = useTranslation();
  if (item?.fulfillment !== "Direct" || !(Number(item.directStockQuantity) > 0)) return null;
  return (
    <span className={`mt-1 block whitespace-normal text-2xs ${item.directReviewedBy ? "text-ink-soft" : "text-warn"}`}>
      {t("deliveryWorkspace.directWithStockAt", { qty: formatQty(Number(item.directStockQuantity)) })}
      {" · "}
      {item.directReviewedBy ? t("deliveryWorkspace.directReviewedBy", { name: item.directReviewedBy }) : t("deliveryWorkspace.directNotReviewed")}
    </span>
  );
}
